import React, { useState, useRef, useEffect } from 'react';
import { X, Camera, Save, Lock, User as UserIcon, Mail, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import UserAvatar from './UserAvatar';
import { sanitizeFilename } from '../utils/fileUtils';
import './UserProfile.css';

const UserProfile = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [fullName, setFullName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  useEffect(() => {
    if (user) {
      setFullName(user.user_metadata?.full_name || '');
    }
  }, [user]);

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setError('File must be an image');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile || !user) return null;
    
    try {
      setUploadingAvatar(true);
      setError('');
      
      // Check if bucket exists first
      const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
      if (bucketsError || !buckets?.find(b => b.name === 'avatars')) {
        throw new Error('Avatar storage not configured. Please contact administrator to set up storage bucket.');
      }
      
      // Generate unique filename with user ID as folder
      // Sanitize the original filename to get a safe extension
      const sanitizedName = sanitizeFilename(avatarFile.name);
      const fileExt = sanitizedName.split('.').pop().toLowerCase();
      const timestamp = Date.now();
      const fileName = `${user.id}/avatar-${timestamp}.${fileExt}`;
      
      // List and delete old avatars in user's folder
      try {
        const { data: oldFiles, error: listError } = await supabase.storage
          .from('avatars')
          .list(user.id, {
            limit: 100
          });
        
        if (!listError && oldFiles && oldFiles.length > 0) {
          const filesToDelete = oldFiles.map(file => `${user.id}/${file.name}`);
          const { error: deleteError } = await supabase.storage
            .from('avatars')
            .remove(filesToDelete);
          
          if (deleteError) {
            console.warn('Could not delete old avatars:', deleteError);
          }
        }
      } catch (cleanupError) {
        console.warn('Avatar cleanup failed:', cleanupError);
        // Continue with upload even if cleanup fails
      }
      
      // Upload new avatar
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, avatarFile, {
          cacheControl: '3600',
          upsert: false,
          contentType: avatarFile.type
        });

      if (uploadError) {
        // Provide user-friendly error messages
        if (uploadError.message?.includes('Bucket not found')) {
          throw new Error('Avatar storage is not set up. Please follow the setup guide in SUPABASE_STORAGE_SETUP.md');
        } else if (uploadError.message?.includes('row level security')) {
          throw new Error('Storage permissions not configured. Please check storage policies.');
        } else if (uploadError.message?.includes('payload too large')) {
          throw new Error('File too large. Please use an image under 5MB.');
        } else if (uploadError.message?.includes('mime type')) {
          throw new Error('Invalid file type. Please use JPG, PNG, GIF, or WebP images.');
        } else {
          throw new Error(`Upload failed: ${uploadError.message}`);
        }
      }
      
      // Get public URL for the uploaded file
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);
      
      return publicUrl || fileName;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      setError(error.message || 'Failed to upload avatar');
      throw error;
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      let avatarUrl = user.user_metadata?.avatar_url;
      
      // Upload avatar if changed
      if (avatarFile) {
        avatarUrl = await uploadAvatar();
      }
      
      // Update user metadata
      const { error: updateError } = await supabase.auth.updateUser({
        data: { 
          full_name: fullName,
          avatar_url: avatarUrl
        }
      });
      
      if (updateError) throw updateError;
      
      setSuccess('Profile updated successfully!');
      setAvatarFile(null);
      setAvatarPreview(null);
      
      // Refresh the page to show updated avatar
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      
      if (newPassword !== confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      
      if (newPassword.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      
      // Verify current password by re-authenticating
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword
      });
      
      if (signInError) {
        setError('Current password is incorrect');
        return;
      }
      
      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (updateError) throw updateError;
      
      setSuccess('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={e => e.stopPropagation()}>
        <div className="profile-header">
          <h2>Profile Settings</h2>
          <button className="profile-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        
        {error && (
          <div className="profile-alert profile-alert-error">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        
        {success && (
          <div className="profile-alert profile-alert-success">
            <AlertCircle size={16} />
            {success}
          </div>
        )}
        
        <div className="profile-content">
          <div className="profile-section">
            <h3>Profile Picture</h3>
            <div className="avatar-section">
              <div className="avatar-preview">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar preview" className="avatar-preview-image" />
                ) : (
                  <UserAvatar user={user} size="large" />
                )}
                <button 
                  className="avatar-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                >
                  <Camera size={16} />
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <div className="avatar-info">
                <p>Upload a new profile picture</p>
                <p className="avatar-hint">JPG, PNG or GIF. Max 5MB</p>
              </div>
            </div>
          </div>
          
          <div className="profile-section">
            <h3>Profile Information</h3>
            <div className="profile-form">
              <div className="form-group">
                <label>
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="form-input disabled"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <UserIcon size={16} />
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  className="form-input"
                />
              </div>
              
              <button
                onClick={handleSaveProfile}
                disabled={loading || (!fullName.trim() && !avatarFile)}
                className="btn btn-primary"
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </div>
          
          <div className="profile-section">
            <h3>Change Password</h3>
            <div className="profile-form">
              <div className="form-group">
                <label>
                  <Lock size={16} />
                  Current Password
                </label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <Lock size={16} />
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>
                  <Lock size={16} />
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="form-input"
                />
              </div>
              
              <button
                onClick={handleChangePassword}
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                className="btn btn-primary"
              >
                <Lock size={16} />
                {loading ? 'Updating...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;